import React, { useState, useEffect } from "react";
import s from "./Users.module.css";

//Assets
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import CreateUser from "./Create";
import Detail from "./Details";

//Servicio
import userService from "../../../api/users/UsersConfig";

//Material Ui
import {
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import GroupIcon from "@mui/icons-material/Group";
import CreateIcon from "@mui/icons-material/Create";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FiltersModal from "./Filters";
import Pager from "../../Shared/Pagination/Pagination";

const cellStyles = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "0.9rem",
  fontWeight: "400",
  borderBottom: "1px solid rgba(0, 0, 0, 0.455)",
};

const UsersRefactor = () => {
  const { t } = useTranslation();
  const { textColor } = useSelector((state) => state.local);
  const { activeOrganization } = useSelector((state) => state.gamma);
  const dispatch = useDispatch();

  //Estados
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [customPage, setCustomPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    name: "lastname",
    increasing: true,
  });

  const [items, setItems] = useState([]);
  const [openError, setOpenError] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState({ status: false, data: null });
  const [create, setCreate] = useState({
    status: false,
    editing: false,
    data: null,
  });

  const getData = () => {
    setLoading(true);
    userService
      .searchUsersByOrganization(
        page,
        pageSize,
        dispatch,
        filters,
        query,
      )
      .then((res) => {
        setItems(res.content);
        setTotalPages(res.totalPages);
        setLoading(false);
      })
      .catch((e) => {
        setItems([]);
        setLoading(false);
        setOpenError(true);
      });
  };

  const handleSort = (item) => {
    filters.name === item
      ? setFilters({ ...filters, increasing: !filters.increasing })
      : setFilters({ name: item, increasing: true });
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: `${t("areYouSure")} ${item?.email}?`,
      text: `${t("ifYouEliminate")} ${item?.firstname} ${item?.lastname} ${t("theChangeCannotBeReversed")}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "green",
      cancelButtonColor: "#d33",
      cancelButtonText: t("cancel"),
      confirmButtonText: t("accept"),
    }).then((result) => {
      if (result.isConfirmed) {
        userService
          .deleteUser(item.id)
          .then(() => {
            Swal.fire(t("deleted"), t("UserDeletedSuccessfully"), "success");
            getData();
          })
          .catch(() => Swal.fire({ icon: "error", title: t("ErrorOcurred") }));
      }
    });
  };

  useEffect(() => {
    getData();
  }, [page, pageSize, filters, query]);

  return (
    <Grid container className={s.container}>
      {detail.status ? (
        <Detail detail={detail} setDetail={setDetail} />
      ) : create.status ? (
        <CreateUser create={create} setCreate={setCreate} getData={getData} />
      ) : (
        <>
          <Grid item xs={12} className={s.header}>
            <Grid item container xs={12} className={s.titleContainer}>
              <Grid
                item
                xs={8}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <GroupIcon
                  sx={{ fontSize: "2rem", color: "primary.main", mr: 2 }}
                />
                <h4>{t("users")}</h4>
                <Button
                  variant="contained"
                  sx={{
                    ml: 1,
                    height: "2.3rem",
                    textTransform: "capitalize",
                    fontSize: "0.9rem",
                    fontWeight: 300,
                  }}
                  onClick={() =>
                    setCreate({
                      status: true,
                      editing: false,
                      data: null,
                    })
                  }
                >
                  {t("add")}
                </Button>
              </Grid>
              <Grid
                item
                xs={3}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  className={s.filterButton}
                  onClick={() => setOpen(true)}
                >
                  <TuneRoundedIcon
                    sx={{ color: "secondary.light", fontSize: "1.8rem" }}
                  />
                  <p>{t("filter")}</p>
                </Button>
                {query.length ? (
                  <Button
                    variant="contained"
                    sx={{ ml: 1 }}
                    className={s.filterButton}
                    onClick={() => setQuery("")}
                  >
                    <p>{t("clearFilters")}</p>
                  </Button>
                ) : null}
              </Grid>
            </Grid>
          </Grid>

          {loading ? (
            <Grid item xs={11} className={s.loader}>
              <CircularProgress size={"4rem"} />
            </Grid>
          ) : items.length ? (
            <Grid item xs={11} className={s.data}>
              <TableContainer className={s.tableContainer}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "primary.main",
                          color: textColor,
                        }}
                        className={s.tableHeader}
                      >
                        {t("state")}
                      </TableCell>
                      <Tooltip title={t("sort")} placement="top-start">
                        <TableCell
                          onClick={() => handleSort("firstname")}
                          sx={{
                            backgroundColor: "primary.main",
                            color: textColor,
                            cursor: "pointer",
                          }}
                          className={s.tableHeader}
                        >{`${t("name")} ${filters.name === "firstname" ? (filters.increasing ? "▼" : "▲") : ""}`}</TableCell>
                      </Tooltip>
                      <Tooltip title={t("sort")} placement="top-start">
                        <TableCell
                          onClick={() => handleSort("lastname")}
                          sx={{
                            backgroundColor: "primary.main",
                            color: textColor,
                            cursor: "pointer",
                          }}
                          className={s.tableHeader}
                        >{`${t("surname")} ${filters.name === "lastname" ? (filters.increasing ? "▼" : "▲") : ""}`}</TableCell>
                      </Tooltip>
                      <TableCell
                        sx={{
                          backgroundColor: "primary.main",
                          color: textColor,
                        }}
                        className={s.tableHeader}
                      >
                        DNI/CUIT
                      </TableCell>
                      <Tooltip title={t("sort")} placement="top-start">
                        <TableCell
                          onClick={() => handleSort("email")}
                          sx={{
                            backgroundColor: "primary.main",
                            color: textColor,
                            cursor: "pointer",
                          }}
                          className={s.tableHeader}
                        >{`${t("mail")} ${filters.name === "email" ? (filters.increasing ? "▼" : "▲") : ""}`}</TableCell>
                      </Tooltip>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "primary.main",
                          color: textColor,
                        }}
                        className={s.tableHeader}
                      >
                        {t("edit")}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          backgroundColor: "primary.main",
                          color: textColor,
                        }}
                        className={s.tableHeader}
                      >
                        {t("delete")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, i) => {
                      return (
                        <React.Fragment key={i}>
                          <TableRow
                            sx={{
                              "&:hover": { backgroundColor: "secondary.light" },
                            }}
                          >
                            <TableCell
                              sx={{
                                ...cellStyles,
                                backgroundColor:
                                  i % 2 ? "#dbdbdb76" : "#dbdbdd12",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                setDetail({ status: true, data: item })
                              }
                            >
                              {item?.enabled ? t("active") : t("inactive")}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyles,
                                backgroundColor:
                                  i % 2 ? "#dbdbdb76" : "#dbdbdd12",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                setDetail({ status: true, data: item })
                              }
                            >
                              {item?.firstname}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyles,
                                backgroundColor:
                                  i % 2 ? "#dbdbdb76" : "#dbdbdd12",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                setDetail({ status: true, data: item })
                              }
                            >
                              {item?.lastname}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyles,
                                backgroundColor:
                                  i % 2 ? "#dbdbdb76" : "#dbdbdd12",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                setDetail({ status: true, data: item })
                              }
                            >
                              {item?.dni || item?.cuit || "-"}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...cellStyles,
                                backgroundColor:
                                  i % 2 ? "#dbdbdb76" : "#dbdbdd12",
                                cursor: "pointer",
                              }}
                              onClick={() =>
                                setDetail({ status: true, data: item })
                              }
                            >
                              {item?.email}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                ...cellStyles,
                                backgroundColor:
                                  i % 2 ? "#dbdbdb76" : "#dbdbdd12",
                              }}
                            >
                              <CreateIcon
                                sx={{
                                  fontSize: "1.8rem",
                                  color: "primary.main",
                                }}
                                className={s.iconbtn}
                                onClick={() =>
                                  setCreate({
                                    status: true,
                                    editing: true,
                                    data: item,
                                  })
                                }
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{
                                ...cellStyles,
                                backgroundColor:
                                  i % 2 ? "#dbdbdb76" : "#dbdbdd12",
                              }}
                            >
                              <DeleteForeverIcon
                                sx={{
                                  fontSize: "1.8rem",
                                  color: "primary.main",
                                }}
                                className={s.iconbtn}
                                onClick={() => handleDelete(item)}
                              />
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          ) : (
            <Grid item xs={11} className={s.loader}>
              {t("noResults")}
            </Grid>
          )}
          <Grid
            item
            container
            xs={11}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              my: 5,
            }}
          >
            <Pager
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              pageCustom={customPage}
              setPageCustom={setCustomPage}
            />
          </Grid>
          <Snackbar
            open={openError}
            autoHideDuration={6000}
            onClose={() => setOpenError(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              variant="filled"
              onClose={() => setOpenError(false)}
              severity="error"
              sx={{ width: "100%" }}
            >
              {t("tryInOtherMoment")}
            </Alert>
          </Snackbar>
        </>
      )}
      {open ? (
        <FiltersModal open={open} setOpen={setOpen} editQuery={setQuery} />
      ) : null}
    </Grid>
  );
};

export default UsersRefactor;
